/**
 * Svelte action for making an element draggable
 * @param {HTMLElement} node - The element to make draggable
 * @param {Object} options - Options for the draggable behavior
 * @returns {Object} - Action lifecycle methods
 */
export const draggable = (node, options = {}) => {
  const {
    handle = node, // Element to use as drag handle
    bounds = "viewport", // 'viewport' or 'parent' or null
    onDragStart = () => {},
    onDrag = () => {},
    onDragEnd = () => {},
  } = options;

  let position = { x: 0, y: 0 };
  let startPosition = { x: 0, y: 0 };
  let isDragging = false;

  const handleMouseDown = (e) => {
    // Only handle left mouse button (0), ignore right-click (2) and middle-click (1)
    if (e.button !== 0) {
      return;
    }

    // Only start drag if clicking on the handle
    if (!handle.contains(e.target)) {
      return;
    }

    // Don't start drag if clicking on interactive elements
    const interactiveElements = ["INPUT", "TEXTAREA", "SELECT", "BUTTON", "A"];
    if (
      interactiveElements.includes(e.target.tagName) ||
      e.target.closest("button, a, input, textarea, select")
    ) {
      return;
    }

    isDragging = true;
    startPosition = {
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    node.style.cursor = "grabbing";
    e.preventDefault();
    onDragStart({ x: position.x, y: position.y });
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;

    let newX = e.clientX - startPosition.x;
    let newY = e.clientY - startPosition.y;

    // Apply bounds if specified
    if (bounds === "viewport") {
      const rect = node.getBoundingClientRect();
      const maxX = window.innerWidth - rect.width;
      const maxY = window.innerHeight - rect.height;

      newX = Math.max(0, Math.min(newX, maxX));
      newY = Math.max(0, Math.min(newY, maxY));
    }

    position = { x: newX, y: newY };
    node.style.transform = `translate(${position.x}px, ${position.y}px)`;

    onDrag({ x: position.x, y: position.y });
  };

  const handleMouseUp = () => {
    if (!isDragging) return;

    isDragging = false;
    node.style.cursor = "";

    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", handleMouseUp);

    onDragEnd({ x: position.x, y: position.y });
  };

  // Touch event handlers
  const handleTouchStart = (e) => {
    if (!handle.contains(e.target)) {
      return;
    }

    const touch = e.touches[0];
    isDragging = true;
    startPosition = {
      x: touch.clientX - position.x,
      y: touch.clientY - position.y,
    };

    document.addEventListener("touchmove", handleTouchMove, { passive: false });
    document.addEventListener("touchend", handleTouchEnd);

    e.preventDefault();
    onDragStart({ x: position.x, y: position.y });
  };

  const handleTouchMove = (e) => {
    if (!isDragging || !e.touches[0]) return;

    const touch = e.touches[0];
    let newX = touch.clientX - startPosition.x;
    let newY = touch.clientY - startPosition.y;

    if (bounds === "viewport") {
      const rect = node.getBoundingClientRect();
      const maxX = window.innerWidth - rect.width;
      const maxY = window.innerHeight - rect.height;

      newX = Math.max(0, Math.min(newX, maxX));
      newY = Math.max(0, Math.min(newY, maxY));
    }

    position = { x: newX, y: newY };
    node.style.transform = `translate(${position.x}px, ${position.y}px)`;

    onDrag({ x: position.x, y: position.y });
  };

  const handleTouchEnd = () => {
    if (!isDragging) return;

    isDragging = false;

    document.removeEventListener("touchmove", handleTouchMove);
    document.removeEventListener("touchend", handleTouchEnd);

    onDragEnd({ x: position.x, y: position.y });
  };

  // Set initial position if provided
  if (options.initialPosition) {
    position = { ...options.initialPosition };
    node.style.transform = `translate(${position.x}px, ${position.y}px)`;
  }

  // Prevent context menu on the draggable element to avoid right-click issues
  const handleContextMenu = (e) => {
    // Only prevent on the drag handle area, not on interactive elements
    if (
      handle.contains(e.target) &&
      !e.target.closest("button, a, input, textarea, select")
    ) {
      e.preventDefault();
    }
  };

  node.addEventListener("mousedown", handleMouseDown);
  node.addEventListener("touchstart", handleTouchStart, { passive: false });
  node.addEventListener("contextmenu", handleContextMenu);

  return {
    update: (newOptions) => {
      Object.assign(options, newOptions);
    },
    destroy: () => {
      // Clean up event listeners
      node.removeEventListener("mousedown", handleMouseDown);
      node.removeEventListener("touchstart", handleTouchStart);
      node.removeEventListener("contextmenu", handleContextMenu);

      // If dragging is active when destroyed, clean up global listeners
      if (isDragging) {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
        document.removeEventListener("touchmove", handleTouchMove);
        document.removeEventListener("touchend", handleTouchEnd);
        isDragging = false;
      }
    },
  };
};
